import { useEffect, useState } from 'react';
import { Auth } from '@/network/RestApi';
import { useAuthStore } from '@/state/authStore';
import { useUIStore } from '@/state/uiStore';

function useViewport() {
  const [vp, setVp] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1280,
    h: typeof window !== 'undefined' ? window.innerHeight : 720,
  }));
  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return vp;
}

// Ornate MU Online style login panel that uses a generated brass-gold frame
// (login_panel.png) as a 9-slice border image. The frame asset is symmetrical
// 1024x1024 with the ornate metalwork occupying the outer ~22% on each side.
// We slice ~225/1024 ~= 22% from each edge, scaled down based on viewport so
// the gems and skull motif read crisply at panel size on both desktop and mobile.
const PANEL_FRAME_URL = '/assets/ui/login/login_panel.png';
const PANEL_SLICE_PX = 225; // source-pixel slice for border-image-slice

export function LoginPanel() {
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [username, setU] = useState('');
  const [password, setP] = useState('');
  const [email, setE] = useState('');
  const [busy, setBusy] = useState(false);
  const setTokens = useAuthStore((s) => s.setTokens);
  const showToast = useUIStore((s) => s.showToast);
  const { w: vw, h: vh } = useViewport();

  // Responsive sizing
  const isMobile = vw < 640;
  const isShort = vh < 620;
  const panelWidth = Math.min(380, vw - 24);
  const borderCss = isMobile ? 36 : 56;
  const panelPad = isMobile ? '14px 16px 16px' : '20px 24px 22px';
  const inputFs = isMobile ? 16 : 14; // 16px to avoid iOS zoom on focus
  const inputPad = isMobile ? '12px 12px' : '10px 12px';
  const titleFs = isMobile ? 14 : 18;
  const titleSpacing = isMobile ? 2 : 4;
  const submitFs = isMobile ? 14 : 15;
  const tabFs = isMobile ? 12 : 13;
  const submitH = isMobile ? 46 : 44;
  const tabH = isMobile ? 40 : 38;

  const ornatePanel: React.CSSProperties = {
    color: '#f3e7c2',
    padding: panelPad,
    borderStyle: 'solid',
    borderWidth: `${borderCss}px`,
    borderImageSource: `url(${PANEL_FRAME_URL})`,
    borderImageSlice: `${PANEL_SLICE_PX} fill`,
    borderImageRepeat: 'stretch',
    borderImageWidth: `${borderCss}px`,
    background: 'linear-gradient(180deg, rgba(20,16,38,0.92) 0%, rgba(8,6,18,0.96) 100%)',
    backgroundClip: 'padding-box',
    boxShadow:
      '0 12px 40px rgba(0,0,0,0.85), 0 0 60px rgba(201,164,74,0.18), inset 0 0 24px rgba(0,0,0,0.7)',
    fontFamily: '"Philosopher", "Trajan Pro", serif',
  };

  const ornateInput: React.CSSProperties = {
    background: 'rgba(8,6,18,0.85)',
    color: '#fff5cc',
    border: '1px solid #6b5320',
    borderTop: '1px solid #3a2c10',
    padding: inputPad,
    borderRadius: 2,
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: '"Philosopher", serif',
    fontSize: inputFs,
    outline: 'none',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.6)',
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    height: tabH,
    background: active
      ? 'linear-gradient(180deg, #c9a44a 0%, #8b6a1f 100%)'
      : 'linear-gradient(180deg, #2a2440 0%, #16122a 100%)',
    color: active ? '#1a1208' : '#cdb98a',
    border: active ? '1px solid #ffd87a' : '1px solid #5a4a2a',
    borderRadius: 2,
    cursor: 'pointer',
    fontWeight: 800,
    letterSpacing: 1,
    fontSize: tabFs,
    fontFamily: '"Philosopher", serif',
    textShadow: active ? 'none' : '0 1px 2px rgba(0,0,0,0.8)',
    boxShadow: active
      ? 'inset 0 -2px 0 rgba(0,0,0,0.35), 0 0 12px rgba(255,216,122,0.4)'
      : 'inset 0 -2px 0 rgba(0,0,0,0.5)',
    transition: 'all 0.15s',
  });

  const submitBtn: React.CSSProperties = {
    height: submitH,
    background: 'linear-gradient(180deg, #b22222 0%, #6e0e0e 100%)',
    color: '#fff5cc',
    border: '1px solid #ffd87a',
    borderRadius: 2,
    cursor: 'pointer',
    fontWeight: 900,
    letterSpacing: 2,
    fontSize: submitFs,
    fontFamily: '"Philosopher", serif',
    textShadow: '0 1px 3px rgba(0,0,0,0.9)',
    boxShadow: 'inset 0 -2px 0 rgba(0,0,0,0.45), 0 0 18px rgba(255,80,80,0.35)',
    transition: 'all 0.15s',
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = tab === 'login'
        ? await Auth.login({ username, password })
        : await Auth.register({ username, email, password });
      setTokens(r.accessToken, r.refreshToken, username);
      showToast(`Đăng nhập thành công`);
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally { setBusy(false); }
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: isShort ? '50%' : '54%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: panelWidth,
        maxHeight: 'calc(100vh - 24px)',
        overflowY: 'auto',
        ...ornatePanel,
      }}
    >
      {/* Decorative banner above tabs */}
      <div
        style={{
          textAlign: 'center',
          color: '#ffd87a',
          fontSize: titleFs,
          fontWeight: 800,
          letterSpacing: titleSpacing,
          marginBottom: isMobile ? 10 : 14,
          textShadow: '0 2px 4px rgba(0,0,0,0.85)',
        }}
      >
        ⚜ TRUYỀN THUYẾT BẮT ĐẦU ⚜
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: isMobile ? 10 : 14 }}>
        <button type="button" onClick={() => setTab('login')} style={tabBtnStyle(tab === 'login')}>
          ĐĂNG NHẬP
        </button>
        <button type="button" onClick={() => setTab('register')} style={tabBtnStyle(tab === 'register')}>
          ĐĂNG KÝ
        </button>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 8 : 10 }}>
        <input
          style={ornateInput}
          placeholder="Tên tài khoản"
          value={username}
          onChange={(e) => setU(e.target.value)}
          required
          minLength={3}
          autoComplete="username"
        />
        {tab === 'register' && (
          <input
            style={ornateInput}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setE(e.target.value)}
            required
            autoComplete="email"
          />
        )}
        <input
          style={ornateInput}
          type="password"
          placeholder="Mật khẩu"
          value={password}
          onChange={(e) => setP(e.target.value)}
          required
          minLength={6}
          autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            ...submitBtn,
            opacity: busy ? 0.6 : 1,
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? '...' : tab === 'login' ? 'TIẾN VÀO AETHERIA' : 'TẠO ANH HÙNG MỚI'}
        </button>
      </form>

      <p
        style={{
          fontSize: 11,
          color: '#a89878',
          marginTop: isMobile ? 10 : 14,
          textAlign: 'center',
          letterSpacing: 1,
          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
        }}
      >
        HERO WORLD - H5 MMORPG
      </p>
    </div>
  );
}
